import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import {
  List,
  ListItemWithLabelsAndAction,
  Sticker,
  TrevorIcon,
  Typography,
} from '@trevor/ui-components';
import { getUserActions } from '@/store-selectors';
import { nanoid } from 'nanoid';
import css from '@/css/module/home.css';
import ModalTip from '@/components/ModalTip';
import styled from 'styled-components';
import { userAction } from '@/util/helpers/ajax-helpers';
import { userIsFinishedOnboarding } from '@/components/HighlightFeature';

type TVideo = {
  title: string;
  length: string;
  youtubeId: string;
};
const videos: { title: string; length: string; youtubeId: string }[] = [
  {
    title: 'Your first query (in under 40 seconds)',
    length: '0:39m',
    youtubeId: '_5bCoPoajMk',
  },
  {
    title: 'Build a dashboard (in 53 seconds)',
    length: '0:53m',
    youtubeId: 'dIqAUVPIcLc',
  },
  {
    title: 'Using SQL in Trevor',
    length: '0:34m',
    youtubeId: '2UtaRaFNbVg',
  },
  {
    title: 'Fundamentals 1: disabling steps',
    length: '0:25m',
    youtubeId: '4g2Wn9GuPIo',
  },
  {
    title: 'Fundamentals 2: drilling down on results',
    length: '0:26m',
    youtubeId: 'zkyVaLOzU9A',
  },
  {
    title: 'Fundamentals 3: jumping between steps',
    length: '0:37m',
    youtubeId: '9quNYhFUcpQ',
  },
  {
    title: 'Fundamentals 4: step order matters',
    length: '0:33m',
    youtubeId: 'MlvrCdQ-Ebc',
  },
  {
    title: 'Export live data to Google Sheets',
    length: '0:45s',
    youtubeId: 'a9TLv09ziEo',
  },
  {
    title: 'Set up your first data alert',
    length: '1:23m',
    youtubeId: 'DNcfNtHDisU',
  },
];

const ListItemWithVideo: FC<TVideo> = ({ title, length, youtubeId }) => {
  return (
    <ModalTip
      title={title}
      body={
        <div className={css.videoWrap}>
          <div className={css.videoContainer}>
            <iframe
              width="620"
              height="349"
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&modestbranding=1&rel=0&theme=light`}
              allow="accelerometer; autoplay; encrypted-media; fullscreen;"
            />
          </div>
        </div>
      }
      medium
    >
      <ListItemWithLabelsAndAction
        onClick={() => userAction('open-tutorial-video', { title })}
        title={title}
        actionLabel="Click to play"
        labels={[<Sticker key="videoLength" iconName="play" label={length} />]}
      />
    </ModalTip>
  );
};

const LetsBuildAQueryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 144px auto;
`;

export const LetsBuildAQuery: FC = () => {
  const userActions = useSelector(getUserActions);
  return (
    <LetsBuildAQueryContainer>
      <div>
        <Typography tag="h1">
          <TrevorIcon
            name="illustration-rocket"
            size="xl"
            style={{ marginRight: 'var(--spacing-l)' }}
          />
          Let's build a query
        </Typography>
        <Typography tag="p" variant="subtitle" style={{ marginTop: '24px' }}>
          Get started by clicking the&nbsp;<b>Start new query</b>&nbsp;button.
        </Typography>
      </div>
      {userIsFinishedOnboarding(userActions) && (
        <div style={{ marginTop: '79px', width: '100%' }}>
          <Typography tag="h2">How-to guides</Typography>
          <List style={{ marginTop: 'var(--spacing-l)' }}>
            {videos.map((video) => {
              const id = nanoid();
              return <ListItemWithVideo key={id} {...video} />;
            })}
          </List>
        </div>
      )}
    </LetsBuildAQueryContainer>
  );
};
